/**
 * @flow
 * @format
 */

import _ from 'lodash';

import { constants } from '~/actions/design';
import appActions from '~/routines/global';
import designRoutines from '~/routines/design';

import { setIn } from '~/utils';

import type { Record } from '~/modules/design/types';

export type CurrentUserType = {
  email?: string,
  id?: string,
  modifiedTime?: string,
  username?: string,
};

export type PartDataType = {
  buyUrl?: string,
  manufacturer?: string,
  partNumber: string,
  productPageUrl?: string,
  sampleUrl?: string,
};

export type BomPartObjectType = {
  [key: string]: PartDataType,
};

export type ChartType = {
  data: Object,
  hasData: boolean,
  loading: boolean,
};

export type RecordsType = {
  loading: boolean,
  data: Map<number, Record>,
  current: ?number,
};

export type GlobalStateType = {
  BOMTable: Array<BomPartObjectType>,
  chartData: ChartType,
  currentUser?: CurrentUserType,
  records: RecordsType,
};

type PayloadType = {|
  data: CurrentUserType | Object,
  message: {},
|};

export type ActionType = {
  payload: PayloadType,
  type: string,
};

type StateType = GlobalStateType;

// Reducer
export const initialState: StateType = {
  BOMTable: [],
  chartData: {
    loading: false,
    hasData: false,
    data: {},
  },
  records: {
    loading: false,
    current: null,
    error: '',
    data: new Map(),
  },
  critChart: {},
  currentUser: undefined,
};

export default function(
  state: StateType = initialState,
  action: ActionType,
): StateType {
  switch (action.type) {
    case constants.UPDATE: {
      const type: string =
        _.get(action, ['payload', 'message', 'type']) ||
        _.get(action, ['payload', 'type'], 'NONE');

      // Short circuit for non-successful responses
      if (type === 'ERROR') return state;

      const BOMTable = _.get(
        action,
        ['payload', 'data', 'BOM', 'currentValue'],
        [],
      );
      const critChart = _.get(action, [
        'payload',
        'data',
        'CritChartData',
        'currentValue',
      ]);

      return {
        ...state,
        chartData: initialState.chartData,
        critChart,
        BOMTable,
      };
    }

    case appActions.fetchChartData.TRIGGER: {
      return {
        ...state,
        chartData: {
          ...state.chartData,
          loading: true,
        },
      };
    }

    case designRoutines.fetchRecord.SUCCESS:
    case appActions.fetchChartData.REQUEST: {
      return {
        ...state,
        chartData: {
          ...state.chartData,
          hasData: false,
          data: {},
        },
      };
    }

    case appActions.fetchChartData.FULFILL: {
      return {
        ...state,
        chartData: {
          ...state.chartData,
          loading: false,
        },
      };
    }

    case appActions.fetchChartData.SUCCESS: {
      let charts = _.get(action.payload, ['response', 'Charts'], []);
      const effSummary = _.get(
        action.payload,
        ['response', 'Powertrain Efficiency Summary'],
        [],
      );

      const loadArray = Array(13)
        .fill(0)
        .map((v, k) => k * 10);
      charts = _.map(charts, (c, i: number) => {
        const cCopy = c;

        // 0-9 are graphs, 10-13 are piecharts
        if (i <= 9) {
          const xaxis = cCopy.data['x-axis'] || loadArray;
          delete cCopy.data['x-axis'];

          cCopy.data = _.set(cCopy.data, 'Unit', xaxis);
        }

        if (cCopy.labels === null) return c;
        cCopy.labels = {
          x: cCopy.labels.xaxis,
          y1: cCopy.labels.yaxis,
        };
        return cCopy;
      });

      return {
        ...state,
        chartData: {
          ...state.chartData,
          hasData: true,
          data: {
            charts,
            effSummary,
          },
        },
      };
    }

    case constants.RECEIVE_USER: {
      const currentUser: CurrentUserType = action.payload.data;
      return {
        ...state,
        currentUser,
      };
    }

    case constants.NEW_DESIGN: {
      return setIn('records.current', null, state);
    }

    case designRoutines.updateRecordName.SUCCESS:
    case designRoutines.deleteDesign.SUCCESS:
    case designRoutines.fetchAllRecords.SUCCESS: {
      // Convert the record array to a Map
      // $FlowIgnore
      const response = action.payload.response;
      if (!response) return state;

      const arr = response.map(record => [record.id, record]);
      const map: Map<number, Record> = new Map(arr);
      return setIn('records.data', map, state);
    }

    // Single record
    case designRoutines.fetchRecord.TRIGGER: {
      return setIn('records.current', Number(action.payload), state);
    }

    case designRoutines.copyDesign.SUCCESS:
    case designRoutines.saveDesignRecord.SUCCESS: {
      // $FlowIgnore
      const response = action.payload.response;
      if (!response) return state;

      const id = response.id;
      const data = response.data;

      // $FlowIgnore
      const arr = data.map(record => [record.id, record]);
      const map: Map<number, Record> = new Map(arr);

      const dataState = setIn('records.data', map, state);

      // skip updating the current design if the modal is open (we're managing)
      // $FlowIgnore
      if (action.payload.fromModal) return dataState;

      return setIn('records.current', id, dataState);
    }

    // Save Design
    case designRoutines.updateRecordName.TRIGGER:
    case designRoutines.copyDesign.TRIGGER:
    case designRoutines.deleteDesign.TRIGGER:
    case designRoutines.fetchAllRecords.TRIGGER:
    case designRoutines.saveDesignRecord.TRIGGER: {
      return setIn('records.loading', true, state);
    }

    case designRoutines.updateRecordName.FAILURE:
    case designRoutines.copyDesign.FAILURE:
    case designRoutines.deleteDesign.FAILURE:
    case designRoutines.saveDesignRecord.FAILURE: {
      return setIn('records.error', action.payload, state);
    }

    case 'HIDE_MODAL': {
      return {
        ...state,
        records: {
          ...state.records,
          loading: false,
          error: '',
        },
      };
    }

    case designRoutines.updateRecordName.FULFILL:
    case designRoutines.copyDesign.FULFILL:
    case designRoutines.deleteDesign.FULFILL:
    case designRoutines.fetchAllRecords.FULFILL:
    case designRoutines.saveDesignRecord.FULFILL: {
      return setIn('records.loading', false, state);
    }

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/global.js